import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const parseBlacklistInput = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const GmailSenderBlacklistDrawer = ({ open, onClose }) => {
  const [blacklists, setBlacklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTermInput, setNewTermInput] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const fetchBlacklists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/gmail-cleanup/sender-blacklist`
      );
      setBlacklists(response.data.blacklists || []);
    } catch (error) {
      console.error("Failed to fetch sender blacklists:", error);
      toast.error("Failed to fetch sender blacklists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchBlacklists();
      setEditingIndex(null);
      setEditingValue("");
      setNewTermInput("");
    }
  }, [open, fetchBlacklists]);

  const saveBlacklists = async (nextBlacklists, successMessage) => {
    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/gmail-cleanup/sender-blacklist`,
        { blacklists: nextBlacklists }
      );
      setBlacklists(nextBlacklists);
      toast.success(successMessage);
    } catch (error) {
      console.error("Failed to update sender blacklists:", error);
      toast.error("Failed to update sender blacklists");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTerms = async () => {
    const newTerms = parseBlacklistInput(newTermInput);
    if (newTerms.length === 0) return;

    const existingLower = new Set(blacklists.map((term) => term.toLowerCase()));
    const uniqueNewTerms = newTerms.filter(
      (term) => !existingLower.has(term.toLowerCase())
    );

    if (uniqueNewTerms.length === 0) {
      toast.info("Those sender terms are already blacklisted");
      setNewTermInput("");
      return;
    }

    await saveBlacklists(
      [...blacklists, ...uniqueNewTerms],
      `${uniqueNewTerms.length} sender term(s) added. Existing processed emails are being rescanned.`
    );
    setNewTermInput("");
  };

  const handleDeleteTerm = async (termToDelete) => {
    const nextBlacklists = blacklists.filter((term) => term !== termToDelete);
    await saveBlacklists(
      nextBlacklists,
      "Sender term removed. Existing processed emails are being rescanned."
    );
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditingValue(blacklists[index]);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleSaveEdit = async () => {
    const trimmed = editingValue.trim();
    if (!trimmed) {
      toast.error("Sender term cannot be empty");
      return;
    }

    const duplicate = blacklists.some(
      (term, index) =>
        index !== editingIndex && term.toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      toast.error("That sender term is already blacklisted");
      return;
    }

    const nextBlacklists = blacklists.map((term, index) =>
      index === editingIndex ? trimmed : term
    );

    await saveBlacklists(
      nextBlacklists,
      "Sender term updated. Existing processed emails are being rescanned."
    );
    setEditingIndex(null);
    setEditingValue("");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 420 },
          padding: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <DoNotTouchIcon color="action" />
          <Typography variant="h6">Global Sender Blacklists</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applies to every connected Gmail account. Emails whose sender address
          or display name partially matches a term are treated as unnecessary and
          removed from the inbox.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            label="Add sender terms"
            variant="outlined"
            size="small"
            value={newTermInput}
            onChange={(event) => setNewTermInput(event.target.value)}
            helperText="Example: talentweb3 matches ged@talentweb3.co"
            disabled={saving}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddTerms();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddTerms}
            disabled={saving || !newTermInput.trim()}
            sx={{ minWidth: 72, alignSelf: "flex-start", mt: 0.25 }}
          >
            Add
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List sx={{ flex: 1, overflow: "auto" }}>
            {blacklists.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                No sender terms blacklisted yet.
              </Typography>
            ) : (
              [...blacklists].reverse().map((term, reverseIndex) => {
                const index = blacklists.length - 1 - reverseIndex;
                const isEditing = editingIndex === index;

                return (
                  <ListItem
                    key={`${term}-${index}`}
                    sx={{
                      px: 0,
                      alignItems: isEditing ? "flex-start" : "center",
                    }}
                    secondaryAction={
                      isEditing ? (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            edge="end"
                            aria-label="save sender term"
                            onClick={handleSaveEdit}
                            disabled={saving}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="cancel edit"
                            onClick={handleCancelEdit}
                            disabled={saving}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            edge="end"
                            aria-label="edit sender term"
                            onClick={() => handleStartEdit(index)}
                            disabled={saving}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete sender term"
                            onClick={() => handleDeleteTerm(term)}
                            disabled={saving}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    }
                  >
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editingValue}
                        onChange={(event) => setEditingValue(event.target.value)}
                        disabled={saving}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSaveEdit();
                          }
                          if (event.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <ListItemText
                        primary={term}
                        sx={{
                          wordBreak: "break-all",
                          pr: 10,
                        }}
                      />
                    )}
                  </ListItem>
                );
              })
            )}
          </List>
        )}

        {saving && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Saving and rescanning processed emails...
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default GmailSenderBlacklistDrawer;
